import { useInvestmentInstrumentDetail, usePortfolioOverviewAssetAllocation, useWealthOverview } from '@egw/api'
import { GenericError, Image, PageHeader } from '@egw/components'
import { DEFAULT_CURRENCY_CODE, type FeatureFlags, WEALTH_ROUTES } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { useRouter } from '@egw/router'
import { buildBasicCurrency } from '@egw/utils'
import { type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useFlags } from '@egw/feature-flags'
import { NotFound404 } from '@egw/features-system'
import { extractHoldingDetail } from '../../../utils'

export const InvestmentsOverviewHoldingsDetails: FC = () => {
  const { t } = useTranslation('features-holdings')

  const isApexAccount = true

  const { slugParams } = useRouter()

  const params = slugParams() as unknown as { assetAllocation: string; category: string; symbol: string }

  const assetAllocationParam = params.assetAllocation

  const categoryParam = params.category

  const symbolParam = params.symbol

  const { data, isLoading: holdingLoading, isError: holdingDetailError } = usePortfolioOverviewAssetAllocation()

  const holdingDetail = extractHoldingDetail({
    data,
    assetAllocationParam,
    categoryParam,
    symbolParam,
  })

  const {
    data: investmetInstrument,
    isLoading: instrumentLoading,
    isError: investmetInstrumentError,
  } = useInvestmentInstrumentDetail(symbolParam)

  const costBasis = holdingDetail?.costBasis
    ? [
        {
          label: t('holding_details.labels.cost_basis'),
          value: buildBasicCurrency({
            amount: holdingDetail.costBasis.amount,
            currencyCode: holdingDetail.costBasis.currencyCode,
          }),
        },
      ]
    : []

  const holdingAttributes =
    holdingDetail && investmetInstrument
      ? [
          {
            label: t('holding_details.labels.holding_name'),
            value: investmetInstrument.name,
          },
          {
            label: t('holding_details.labels.previous_close'),
            value: investmetInstrument.priorClosePrice?.amount
              ? buildBasicCurrency({
                  amount: investmetInstrument.priorClosePrice.amount,
                  currencyCode: DEFAULT_CURRENCY_CODE,
                })
              : t('holding_details.empty_value'),
          },
          {
            label: t('holding_details.labels.quantity_owned'),
            value: holdingDetail.quantity
              ? t('holding_details.shares', { quantity: Number(holdingDetail.quantity).toFixed(2) })
              : t('holding_details.empty_value'),
          },
          {
            label: t(`holding_details.labels.amount_owned`),
            value: buildBasicCurrency({
              amount: holdingDetail.amount.amount,
              currencyCode: holdingDetail.amount.currencyCode,
            }),
          },
          ...costBasis,
        ]
      : []

  return isApexAccount ? (
    <NotFound404 />
  ) : (
    <>
      <PageHeader
        title={t('holding_details.title')}
        subtitle={t(`category_titles.${categoryParam}`)}
        backLinkUrl={WEALTH_ROUTES.INVESTMENTS_HOLDINGS_DETAILS_OVERVIEW.backPath
          ?.replace(':assetAllocation', assetAllocationParam)
          .replace(':category', categoryParam)}
      />
      <div className="egw-container pt-0">
        {instrumentLoading && holdingLoading ? (
          <div className="w-full">
            <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
              <Skeleton count={1} height="100px" className="mb-10" />
              <Skeleton count={5} height="50px" />
            </SkeletonTheme>
          </div>
        ) : null}
        {investmetInstrumentError || holdingDetailError ? (
          <div className="default-page-width my-20">
            <GenericError errorMessage={t('holdings.overview_fatal_error')} />
          </div>
        ) : null}
        {holdingDetail && investmetInstrument ? (
          <div className="flex flex-col font-light gap-8">
            <div className="text-meta text-3xl flex flex-row gap-6 items-center">
              {investmetInstrument.image ? (
                <Image src={investmetInstrument.image} alt="holding image" className="w-16 h-16" />
              ) : null}
              {investmetInstrument.symbol}
            </div>
            <div className="flex flex-col">
              {holdingAttributes.map(({ label, value }: { label: string; value: string }) => (
                <div
                  key={crypto.randomUUID()}
                  className="border-light flex flex-row justify-between border-b py-4 last-of-type:border-none"
                >
                  <span className="text-meta text-base">{label}</span>
                  <span className="text-base text-header font-semibold ">{value}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </>
  )
}
